import { isNetworkError } from '@portal-packages/api';
import { AxiosError } from 'axios';
import { IntlShape, MessageDescriptor } from 'gatsby-plugin-intl';
import is from 'ramda/es/is';
import { MutableRefObject } from 'react';

import { JWTPayload } from 'types/api';
import { errorCodeIs } from 'utils/error';
import { StoragePath, getStaticHostURL } from 'utils/static';

import {
  ChangeDeveloperPasswordErrorCodes,
  CreateDeveloperErrorCodes,
  ResetDeveloperPasswordErrorCodes,
} from '../constants';
import {
  Developer,
  DeveloperUpdate,
  GetDeveloperResponse,
  UpdateDeveloperFormValidated,
  UpdateDeveloperFormValidationResult,
  UpdateDeveloperFormValues,
} from '../types';

const isAccountRegisteredError = errorCodeIs(CreateDeveloperErrorCodes.ACCOUNT_ALREADY_REGISTERED);
const isCPFConflictError = errorCodeIs(CreateDeveloperErrorCodes.CPF_ALREADY_IN_USE);
const isCNPJConflictError = errorCodeIs(CreateDeveloperErrorCodes.CNPJ_ALREADY_IN_USE);
const isSlugConflictError = errorCodeIs(CreateDeveloperErrorCodes.SLUG_ALREADY_EXISTS);
const isTextViolationError = errorCodeIs(CreateDeveloperErrorCodes.TEXT_VIOLATION_01);
const isInviteExpiredError = errorCodeIs(CreateDeveloperErrorCodes.INVITE_EXPIRED);

export const handleCreateDeveloperError = (error: AxiosError): MessageDescriptor => {
  if (isAccountRegisteredError(error)) {
    return { id: 'error.create-developer.ACCOUNT_ALREADY_REGISTERED' };
  }

  if (isCPFConflictError(error)) {
    return { id: 'error.create-developer.CPF_ALREADY_IN_USE' };
  }

  if (isCNPJConflictError(error)) {
    return { id: 'error.create-developer.CNPJ_ALREADY_IN_USE' };
  }

  if (isSlugConflictError(error)) {
    return { id: 'error.create-developer.SLUG_ALREADY_EXISTS' };
  }

  if (isTextViolationError(error)) {
    return { id: 'error.create-developer.TEXT_VIOLATION_CODE' };
  }

  if (isNetworkError(error)) {
    return { id: 'error.create-developer.NET_ERR' };
  }

  if (isInviteExpiredError(error)) {
    return { id: 'error.create-developer.INVITE_EXPIRED' };
  }

  return { id: 'error.create-developer.UNKNOWN' };
};

const isInvalidPasswordError = errorCodeIs(ChangeDeveloperPasswordErrorCodes.INCORRECT_CURRENT_PASSWORD);

export const handleChangeDeveloperPasswordError = (error: AxiosError): MessageDescriptor => {
  if (isInvalidPasswordError(error)) {
    return { id: 'error.change-password.INCORRECT_CURRENT_PASSWORD' };
  }

  if (isNetworkError(error)) {
    return { id: 'error.change-password.NET_ERR' };
  }

  return { id: 'error.request.unexpected-error' };
};

const isAccountNotFoundError = errorCodeIs(ResetDeveloperPasswordErrorCodes.ACCOUNT_NOT_FOUND);

export const handleResetDeveloperPasswordError = (error: AxiosError): ResetDeveloperPasswordErrorCodes => {
  if (isAccountNotFoundError(error)) {
    return ResetDeveloperPasswordErrorCodes.ACCOUNT_NOT_FOUND;
  }

  if (isNetworkError(error)) {
    return ResetDeveloperPasswordErrorCodes.NET_ERR;
  }

  return ResetDeveloperPasswordErrorCodes.UNKNOWN;
};

export const toDeveloper = ({ teams: [team], developer }: GetDeveloperResponse, jwt: JWTPayload): Developer => {
  return {
    ...developer,
    realm_access: jwt?.realm_access ?? {
      roles: [],
    },
    role: team.role,
    accountId: team.id,
    photo: developer.photo ? getStaticHostURL(developer.photo, StoragePath.ProfileAvatar) : undefined,
  };
};

export const handleUpdateDeveloperError = (error: AxiosError): MessageDescriptor => {
  if (isTextViolationError(error)) {
    return { id: 'error.request.update-developer.TEXT_VIOLATION_CODE' };
  }

  return { id: 'error.request.update-developer' };
};

export const toDeveloperUpdate = (update: UpdateDeveloperFormValidated): DeveloperUpdate => ({
  name: update.name,
  photo: is(String, update.photo) ? undefined : (update.photo as File),
});

export const validateUpdateDeveloperForm =
  ({ formatMessage }: IntlShape, asyncErrors: MutableRefObject<Record<string, string | undefined>>) =>
    (values: UpdateDeveloperFormValues): UpdateDeveloperFormValidationResult => {
      const errors: UpdateDeveloperFormValidationResult = asyncErrors.current;

      if (!values.name?.trim()) {
        errors.name = formatMessage({ id: 'validations.field-required' });
      }

      return errors;
    };
